import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom"; 
import axios from "axios";
import "./AnticipationGame.css";

const AnticipationGame = () => {
  const navigate = useNavigate();

  const timerRef = useRef(null);
  const ballRef = useRef({ x: 0, y: 0 });

  const [testNumber, setTestNumber] = useState(0);
  const [phase, setPhase] = useState("move");

  const [ball, setBall] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(true);
  const [showLines, setShowLines] = useState(false);

  const [mouseY, setMouseY] = useState(window.innerHeight / 2);

  const [startTime, setStartTime] = useState(0);
  const [testerStartTime, setTesterStartTime] = useState(0);

  const [result, setResult] = useState(null);
  const [allResults, setAllResults] = useState([]); // 🔥 NEW

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const leftLine = 0.4 * screenWidth;
  const rightLine = 0.6 * screenWidth;

  const ballSize = Math.min(screenWidth, screenHeight) * 0.05;

  const linearConfigs = [
    { startY: 0.2, speedY: 1 },
    { startY: 0.2, speedY: 1 },
    { startY: 0.8, speedY: -1 },
    { startY: 0.8, speedY: -1 }
  ];

  const sineConfigs = [
    { amplitude: 0.12, waves: 1 },
    { amplitude: 0.15, waves: 1.5 },
    { amplitude: 0.18, waves: 2 },
    { amplitude: 0.22, waves: 2.5 },
    { amplitude: 0.25, waves: 3 }
  ];

  let speedX = testNumber % 2 === 0 ? 2 : -2;

  useEffect(() => {
    startGame();
    return () => clearTimeout(timerRef.current);
  }, [testNumber]);

  const startGame = () => {

    clearTimeout(timerRef.current);

    let startX = testNumber % 2 === 0 ? 50 : screenWidth - 50;
    let startY;

    if (testNumber < 4) {
      startY = screenHeight * linearConfigs[testNumber].startY;
    } else {
      startY = screenHeight / 2;
    }

    ballRef.current = { x: startX, y: startY };
    setBall(ballRef.current);

    setVisible(true);
    setShowLines(false);
    setPhase("move");

    setStartTime(performance.now());

    move();
  };

  const move = () => {

    let { x, y } = ballRef.current;

    const shouldMove =
      testNumber % 2 === 0
        ? (x + ballSize) < leftLine
        : x > rightLine;

    if (!shouldMove) {
      ballHitLine();
      return;
    }

    x += speedX;

    if (testNumber < 4) {
      y += linearConfigs[testNumber].speedY;
    } else {
      const config = sineConfigs[testNumber - 4];

      const progress =
        testNumber % 2 === 0
          ? (x - 50) / (leftLine - 50)
          : (screenWidth - 50 - x) / (screenWidth - 50 - rightLine);

      const clamped = Math.max(0, Math.min(1, progress));
      const amplitude = screenHeight * config.amplitude;

      y =
        screenHeight / 2 +
        amplitude *
        Math.sin(clamped * Math.PI * 2 * config.waves);
    }

    ballRef.current = { x, y };
    setBall({ x, y });

    if (testNumber % 2 === 0) {
      if (x > 0.7 * leftLine && x < 0.7 * leftLine + speedX) {
        setShowLines(true);
      }
    } else {
      if (
        x < rightLine + 0.3 * (screenWidth - rightLine) &&
        x > rightLine + 0.15 * (screenWidth - rightLine)
      ) {
        setShowLines(true);
      }
    }

    timerRef.current = setTimeout(move, 20);
  };

  const ballHitLine = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
    setPhase("predict");
    setTesterStartTime(performance.now());
  };

  const estimateY = (futureX) => {

    if (testNumber < 4) {
      const { x, y } = ballRef.current;
      return y + linearConfigs[testNumber].speedY *
        ((futureX - x) / speedX);
    } else {
      const config = sineConfigs[testNumber - 4];

      const progress =
        testNumber % 2 === 0
          ? (futureX - 50) / (leftLine - 50)
          : (screenWidth - 50 - futureX) / (screenWidth - 50 - rightLine);

      const clamped = Math.max(0, Math.min(1, progress));
      const amplitude = screenHeight * config.amplitude;

      return (
        screenHeight / 2 +
        amplitude *
        Math.sin(clamped * Math.PI * 2 * config.waves)
      );
    }
  };

  const handleClick = () => {

  if (phase !== "predict") return;

  const testerEnd = performance.now();
  const testerTime = (testerEnd - testerStartTime) / 1000;

  const futureX =
    testNumber % 2 === 0 ? rightLine : leftLine;

  const expectedY = estimateY(futureX);

  const pixels_in_mm = 3.78;
  const dpi = pixels_in_mm * 25.4;

  const diffPx = mouseY - expectedY;
  const diffCm = (diffPx * 2.54) / dpi;

  const totalTime = (performance.now() - startTime) / 1000;

  const distanceCovered =
    testNumber % 2 === 0
      ? leftLine - 50
      : (screenWidth - 50) - rightLine;

  const distanceBetweenLines = rightLine - leftLine;

  const expectedTime =
    (distanceBetweenLines * totalTime) / distanceCovered;

  const timeError = testerTime - expectedTime;

  const finalData = {
    game: testNumber < 4 ? "Linear" : "Sine",
    test: testNumber + 1,
    distanceError: Number(diffCm.toFixed(2)),
    timeError: Number(timeError.toFixed(2)),
    expectedY,
    clickY: mouseY
  };

  // 🔥 STORE RESULT (NO SCORE)
  setAllResults(prev => [
    ...prev,
    {
      testNumber: testNumber + 1,
      GameName: finalData.game,
      DistanceError: `${finalData.distanceError}cm`,
      TimeError: finalData.timeError,

      ExpectedBallY: finalData.expectedY,
      userClickBallY: finalData.clickY,

      BallX: ballRef.current.x,
      BallY: ballRef.current.y,

      speedX,
      speedY: testNumber < 4 ? linearConfigs[testNumber].speedY : 0,

      amplitude: testNumber >= 4 ? sineConfigs[testNumber - 4].amplitude : null,
      periodicity: testNumber >= 4 ? 0.008 : null,

      LeftLine: leftLine,
      RightLine: rightLine
    }
  ]);

  setBall({ x: futureX, y: expectedY });
  setVisible(true);
  setResult(finalData);
  setPhase("result");
};

  const next = async () => {

    if (testNumber >= 8) {

      try {
        const email = localStorage.getItem("rg_email");

        await axios.post("/save-anticipation", {
          email,
          results: allResults
        });

        alert("Saved successfully ✅");

      } catch (e) {
        console.log("SAVE ERROR:", e.response?.data || e.message);
      }

      return;
    }

    setResult(null);
    setMouseY(screenHeight / 2);
    setTestNumber(prev => prev + 1);
  };
  const finish = async () => {

  const email = localStorage.getItem("rg_email");

  try {
    const res = await axios.post("/save-anticipation", {
      email,
      results: allResults
    });

    console.log("Saved ✅");

    // 🔥 GET BACKEND SCORE
    const score = res.data.score;

    // 🔥 PASS TO RESULT PAGE
    navigate("/anticipation/result", {
      state: { 
        results: allResults,
        score: score   // 🔥 IMPORTANT
      }
    });

  } catch (e) {
    console.log("SAVE ERROR:", e.response?.data || e.message);

    // fallback navigation
    navigate("/anticipation/result", {
      state: { results: allResults }
    });
  }
};

  return (
    <div
      className="anticipation-container"
      onMouseMove={(e) => {
        if (phase === "predict") setMouseY(e.clientY);
      }}
      onClick={handleClick}
      style={{
        cursor: phase === "predict" ? "none" : "default"
      }}
    >

      {visible && (
        <div
          className="ball"
          style={{
            left: ball.x,
            top: ball.y,
            width: ballSize,
            height: ballSize
          }}
        />
      )}

      {showLines && (
        <>
          <div className="line" style={{ left: leftLine }} />
          <div className="line" style={{ left: rightLine }} />
        </>
      )}

      {phase === "predict" && (
        <div
          className="arrow"
          style={{
            top: mouseY,
            left: testNumber % 2 === 0 ? rightLine : leftLine
          }}
        />
      )}

      {phase === "result" && result && (
        <div className="result-box">
          <h2>Test {result.test}</h2>
          <p>{result.distanceError} cm</p>
          <p>
            {Math.abs(result.timeError)}s{" "}
            {result.timeError > 0 ? "slow" : "fast"}
          </p>
          {testNumber === 8 ? (
            <button onClick={finish}>Finish</button>
          ) : (
            <button onClick={next}>Next</button>
          )}
        </div>
      )}

    </div>
  );
};

export default AnticipationGame;